import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useState } from "react";

import ContactModal from "../../../../Assets/Common/ContactModal";

export default function CTASection() {
  const [openModal, setOpenModal] = useState(false);

  const faqs = [
    {
      question: "What SMS and OTP services do you provide?",
      answer:
        "Our platform provides OTP verification, transactional SMS, promotional messaging, bulk SMS, and secure SMS API integration for websites, mobile applications, CRMs, ERPs, and enterprise software.",
    },
    {
      question: "Can I integrate SMS and OTP services with my application?",
      answer:
        "Yes. Our developer-friendly APIs allow you to integrate OTP and SMS functionality with websites, Android and iOS applications, CRMs, ERPs, eCommerce platforms, and custom business software.",
    },
    {
      question: "How quickly are OTP messages delivered?",
      answer:
        "Our OTP infrastructure is optimized for high-speed delivery through reliable SMS routes, helping businesses deliver authentication codes within seconds under normal network conditions.",
    },
    {
      question: "Is the SMS platform DLT compliant?",
      answer:
        "Yes. The platform supports DLT-compliant messaging workflows, including sender ID and message template requirements applicable to business messaging in India.",
    },
    {
      question: "Is OTP messaging secure?",
      answer:
        "Yes. OTP workflows are designed around secure authentication practices, time-sensitive verification codes, API authentication, controlled request handling, and reliable delivery infrastructure.",
    },
    {
      question: "Can the service handle high-volume SMS and OTP traffic?",
      answer:
        "Yes. The infrastructure is designed for scalable messaging workloads, making it suitable for startups, growing businesses, and enterprises sending large volumes of transactional, promotional, and authentication messages.",
    },
  ];

  return (
    <>
      {/* ============================================================
          FAQ SECTION
      ============================================================ */}
      <section
        className="
          py-16
          font-saira
          theme-bg-primary
        "
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          {/* ========================================================
              FAQ HEADING
          ======================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            {/* Eyebrow */}
            <span
              className="
                uppercase
                tracking-[4px]
                text-sm
                font-semibold
                brand-color
              "
            >
              Frequently Asked Questions
            </span>

            {/* Heading */}
            <h2
              className="
                mt-5
                text-4xl
                lg:text-5xl
                font-bold
                theme-text-primary
              "
            >
              Everything You Need to Know
            </h2>

            {/* Description */}
            <p
              className="
                mt-6
                leading-8
                theme-text-muted
              "
            >
              Get answers about OTP verification, Business SMS, API
              integration, delivery, security, compliance, scalability, and
              enterprise messaging.
            </p>
          </motion.div>

          {/* ========================================================
              FAQ LIST
          ======================================================== */}
          <div className="max-w-7xl mx-auto mt-10 space-y-6">
            {faqs.map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  delay: index * 0.1,
                }}
                className="
                  group
                  rounded-2xl
                  border
                  theme-border
                  theme-card
                  p-6
                  transition-all
                  duration-300
                  hover:border-orange-500/30
                "
              >
                <summary
                  className="
                    flex
                    cursor-pointer
                    list-none
                    items-center
                    justify-between
                    gap-5
                    text-lg
                    font-semibold
                    theme-text-primary
                  "
                >
                  <span>{faq.question}</span>

                  <span
                    className="
                      shrink-0
                      text-2xl
                      brand-color
                      transition-transform
                      duration-300
                      group-open:rotate-45
                    "
                  >
                    +
                  </span>
                </summary>

                <p
                  className="
                    mt-5
                    leading-8
                    theme-text-muted
                  "
                >
                  {faq.answer}
                </p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA SECTION
      ============================================================ */}
      <section
        className="
          relative
          py-10
          overflow-hidden
          font-saira
          theme-bg-primary
        "
      >
        {/* ==========================================================
            BACKGROUND GLOW
        ========================================================== */}
        <div
          className="
            absolute
            left-0
            top-0
            h-72
            w-72
            rounded-full
            bg-orange-500/10
            blur-[140px]
            pointer-events-none
          "
        />

        <div
          className="
            absolute
            right-0
            bottom-0
            h-72
            w-72
            rounded-full
            bg-orange-500/10
            blur-[140px]
            pointer-events-none
          "
        />

        {/* ==========================================================
            MAIN CONTAINER
        ========================================================== */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="
              relative
              rounded-3xl
              border
              theme-border
              theme-card
              px-8
              py-12
              lg:px-16
              lg:py-14
              text-center
            "
          >
            {/* ======================================================
                BADGE
            ====================================================== */}
            <span
              className="
                inline-flex
                items-center
                rounded-full
                border
                border-orange-500/20
                bg-orange-500/10
                px-4
                py-2
                text-sm
                font-medium
                brand-color
              "
            >
              Ready to Power Your Communication?
            </span>

            {/* ======================================================
                HEADING
            ====================================================== */}
            <h2
              className="
                mt-6
                mx-auto
                max-w-5xl
                text-3xl
                lg:text-5xl
                font-bold
                leading-[1.15]
                theme-text-primary
              "
            >
              One Powerful Platform for SMS & OTP
              <br />

              <span className="brand-color">
                Communication
              </span>
            </h2>

            {/* ======================================================
                DESCRIPTION
            ====================================================== */}
            <p
              className="
                mx-auto
                mt-5
                max-w-4xl
                leading-7
                theme-text-muted
              "
            >
              Deliver secure OTP verification, transactional notifications,
              promotional campaigns, bulk SMS, and API-powered messaging
              through a reliable communication platform built for modern
              businesses.
            </p>

            {/* ======================================================
                CTA BUTTON
            ====================================================== */}
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <button
                onClick={() => {
                  setOpenModal(true);
                }}
                className="
                  group
                  flex
                  items-center
                  gap-2
                  rounded-xl
                  bg-orange-500
                  px-7
                  py-3.5
                  font-semibold
                  text-white
                  transition-all
                  duration-300
                  hover:bg-orange-600
                  hover:shadow-[0_12px_35px_rgba(249,115,22,0.25)]
                "
              >
                Get Started

                <ArrowRight
                  size={18}
                  className="
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </button>
            </div>
          </motion.div>
        </div>

        {/* ==========================================================
            CONTACT MODAL
        ========================================================== */}
        <ContactModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          initialService="API Services"
          initialSubService="SMS OTP Services"
        />
      </section>
    </>
  );
}